import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'

interface ProjectBrief {
  id: string
  services: string[]
  discovery_meeting_only: boolean
  company_goals: string | null
  budget: string | null
  status: string
  created_at: string
  business_id: string | null
  businesses: { id: string; name: string } | null
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  in_progress: 'In progress',
  won: 'Won',
  lost: 'Lost',
}

export default function PortalDashboard() {
  const { user } = useAuth()
  const { canSeeClients } = usePlatformRoleContext()

  const { data: projectBriefs, isLoading: briefsLoading } = useQuery({
    queryKey: ['project-briefs', user?.id],
    queryFn: async (): Promise<ProjectBrief[]> => {
      const { data, error } = await supabase
        .from('project_briefs')
        .select('id, services, discovery_meeting_only, company_goals, budget, status, created_at, business_id, businesses(id, name)')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as ProjectBrief[]
    },
    enabled: !!user?.id,
  })

  const briefsByBusiness = useMemo(() => {
    if (!projectBriefs) return []
    const groups: { businessId: string | null; businessName: string; briefs: ProjectBrief[] }[] = []
    const seen = new Map<string | null, number>()
    for (const b of projectBriefs) {
      const key = b.business_id
      const name = b.businesses?.name ?? (b.business_id ? 'Unknown business' : 'No business')
      if (!seen.has(key)) {
        seen.set(key, groups.length)
        groups.push({ businessId: b.business_id, businessName: name, briefs: [] })
      }
      groups[seen.get(key)!].briefs.push(b)
    }
    groups.sort((a, b) => {
      if (a.businessName === 'No business') return 1
      if (b.businessName === 'No business') return -1
      return a.businessName.localeCompare(b.businessName)
    })
    return groups
  }, [projectBriefs])

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">Dashboard</h1>
        <p className="text-white/60 text-sm mt-1">Welcome back. Here are your project briefs.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm"
      >
        <h2 className="text-lg font-heading font-semibold text-white mb-4">Project briefs</h2>
        {briefsLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/50" />
          </div>
        ) : !briefsByBusiness || briefsByBusiness.length === 0 ? (
          <p className="text-white/50 text-sm">You haven&apos;t submitted any project briefs yet.</p>
        ) : (
          <div className="space-y-6">
            {briefsByBusiness.map((group, gi) => (
              <motion.div
                key={group.businessId ?? group.businessName}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.1 + gi * 0.03 }}
              >
                <h3 className="text-sm font-heading uppercase tracking-wide text-white/70 mb-3">
                  {group.businessName}
                </h3>
                <div className="space-y-2">
                  {group.briefs.map((brief) => (
                    <div
                      key={brief.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 px-4 rounded-lg border border-white/10 bg-white/[0.02]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-heading uppercase tracking-wide text-white/50 px-2 py-0.5 rounded border border-white/20 bg-white/5">
                            {STATUS_LABELS[brief.status] ?? brief.status}
                          </span>
                          <span className="text-xs text-white/40">
                            {new Date(brief.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-white/90 text-sm mt-1">
                          {brief.discovery_meeting_only
                            ? 'Discovery meeting only'
                            : brief.services.length > 0
                              ? brief.services.join(', ')
                              : brief.company_goals?.slice(0, 80) ?? 'No details'}
                          {brief.company_goals && !brief.discovery_meeting_only && brief.services.length > 0
                            ? ` — ${brief.company_goals.slice(0, 60)}${brief.company_goals.length > 60 ? '…' : ''}`
                            : ''}
                        </p>
                      </div>
                      {canSeeClients && group.businessId && (
                        <Link
                          to={`/portal/businesses/${group.businessId}`}
                          className="shrink-0 text-sm font-medium text-white/80 hover:text-white transition-colors"
                        >
                          View business →
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  )
}
